/* Único punto de verdad sobre la conexión con el companion (el motor local). */

export const COMPANION = "https://localhost:3001";

/** Reporta un error del panel al motor (Office.js, fetch, "Load failed") → diagnóstico de Rais. Anónimo. */
export function reportPanelError(where: string, e: unknown): void {
  try {
    const err = e as { name?: string; message?: string; stack?: string };
    fetch(`${COMPANION}/error`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        where,
        type: err?.name || "Error",
        message: String(err?.message ?? err ?? "").slice(0, 200),
        stack: String(err?.stack || "").slice(0, 300),
      }),
    }).catch(() => {});
  } catch {
    /* nunca debe afectar al panel */
  }
}

/**
 * Qué operación produjo cada propuesta.
 *
 * El motor devuelve un identificador de operación en la cabecera `x-hi-op`, y es lo que después
 * permite decir «esta propuesta el autor la conservó» en vez de deducirlo. Aquí se guarda la
 * correspondencia texto → operación, porque entre que el colaborador responde y el autor inserta
 * pasan por medio la interfaz y su decisión, y el identificador no puede perderse por el camino.
 *
 * Si no aparece —una versión anterior del motor, un fallo— el registro sigue funcionando como
 * antes, por presencia. Nunca se inventa un identificador.
 */
const opsPorTexto = new Map<string, string>();
const clave = (t: string) => String(t || "").trim().slice(0, 120);

export function recordarOp(texto: string, op: string | null): void {
  if (!op || !texto) return;
  if (opsPorTexto.size > 200) opsPorTexto.clear();   // el panel vive mucho; la memoria no es infinita
  opsPorTexto.set(clave(texto), op);
}

export function opDe(texto: string): string | null {
  return opsPorTexto.get(clave(texto)) ?? null;
}

/** Registra en el companion una reescritura de IA insertada (para el HAS del certificado). */
export function logRewrite(text: string, doc?: string): void {
  if (!text || text.trim().length < 8) return;
  try {
    fetch(`${COMPANION}/awap/insertion`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ text, ref: opDe(text), doc: doc ?? null }),
    }).catch(() => {});
  } catch {
    /* nunca debe afectar a la inserción en Word */
  }
}

/** Lo que el autor hizo con una propuesta, deducido del documento. Nunca se marca como declarado. */
export function registrarDecision(
  ref: string, decision: string, similitud: number, dir: string | null, doc: string | null,
): void {
  try {
    fetch(`${COMPANION}/decision`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ref, decision, metodo: "reconciliacion", similitud, dir, doc }),
    }).catch(() => {});
  } catch {
    /* el panel nunca se cae por esto */
  }
}

/**
 * Mensaje de error ÚTIL cuando el fetch al motor falla. "Load failed" puede ser dos cosas
 * muy distintas — motor caído o certificado sin confianza (el antivirus suele romper esa
 * confianza durante la instalación) — y el usuario puede distinguirlas él mismo en Safari.
 */
export function companionError(e: unknown): string {
  const detail = (e as any)?.message ? ` (${(e as any).message})` : "";
  return (
    "No se pudo conectar con el motor de HumanInk" +
    detail +
    ". Prueba a abrir https://localhost:3001/health en Safari: " +
    "si no responde, el motor está apagado (reinicia sesión en el Mac o reinstala); " +
    "si Safari avisa de seguridad, reinstala el certificado — guía: word.humanink.io/instalar#problemas"
  );
}

/**
 * Traduce el error que el motor devuelve cuando la llamada al proveedor falla.
 *
 * El cuerpo llega crudo desde OpenRouter — `{"error":{"message":"User not found.","code":401}}` —
 * y para el autor no significa nada: cree que el "usuario" es su cuenta de HumanInk cuando lo que
 * falla es su clave de OpenRouter. Cada causa tiene una acción distinta, así que se nombra.
 */
export function providerError(label: string, status: number, body: string): string {
  const crudo = body.slice(0, 300);
  // El motor ya explica una respuesta cortada (1.6.26): se enseña su frase, no el JSON.
  try { const j = JSON.parse(body); if (j?.codigo === "cortada" && typeof j.error === "string") return j.error; } catch { /* no es JSON */ }
  const dice = (s: string) => body.toLowerCase().includes(s);

  // La clave tiene un tope de gasto y ha llegado: no es falta de saldo en la cuenta ni una clave mala.
  if (dice("key limit exceeded") || dice("tope de gasto")) {
    return (
      "Tu clave de OpenRouter ha llegado al tope de gasto que tiene puesto. " +
      "Súbelo en openrouter.ai (Settings → Keys, en esa clave) o pon otra clave en Ajustes, y vuelve a intentarlo."
    );
  }
  if (status === 401 || dice("user not found") || dice("no auth credentials")) {
    return (
      "Tu clave de OpenRouter no es válida (el proveedor responde «User not found»). " +
      "Suele pasar si se ha borrado o regenerado la clave. " +
      "Entra en openrouter.ai/keys, crea una nueva y pégala en Ajustes → Clave de OpenRouter."
    );
  }
  if (status === 402 || dice("insufficient") || dice("credits")) {
    return (
      "Tu cuenta de OpenRouter se ha quedado sin saldo. " +
      "Recárgala en openrouter.ai/credits y vuelve a intentarlo."
    );
  }
  if (status === 429 || dice("rate limit")) {
    return "OpenRouter está limitando las peticiones. Espera un minuto y reintenta.";
  }
  if (status === 403 && dice("moderation")) {
    return (
      "El proveedor ha bloqueado este fragmento por sus filtros de contenido. " +
      "Prueba con otro modelo en el desplegable, o con un fragmento más corto."
    );
  }
  // Lo que diga el motor con sus palabras, antes que el JSON en crudo.
  try {
    const j = JSON.parse(body);
    const dicho = typeof j?.error === "string" ? j.error : j?.error?.message;
    if (typeof dicho === "string" && dicho.trim()) return dicho;
  } catch { /* no es JSON */ }
  return `${label} ${status}: ${crudo}`;
}

/** ¿La versión anunciada por el servidor es posterior a la instalada?
 *
 * Compara número a número, no como texto: "1.10.0" es POSTERIOR a "1.9.0" aunque alfabéticamente
 * vaya antes. En cuanto el minor pase de 9 —y con el ritmo de versiones de este producto pasará—
 * una comparación de cadenas empezaría a decir que la nueva es más vieja y el aviso no saldría.
 *
 * Ante cualquier duda devuelve false: un aviso que no aparece es un fastidio; uno que aparece
 * cuando ya estás al día es ruido que enseña a ignorar los avisos.
 */
export function hayVersionNueva(actual?: string | null, ultima?: string | null): boolean {
  if (!actual || !ultima) return false;
  const trozos = (v: string) => v.trim().replace(/^v/i, "").split(".").map((x) => parseInt(x, 10));
  const a = trozos(actual), b = trozos(ultima);
  if (a.some(isNaN) || b.some(isNaN) || !a.length || !b.length) return false;
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0, y = b[i] ?? 0;
    if (y > x) return true;
    if (y < x) return false;
  }
  return false;
}
